
import { Product } from '@/model/product';
import { GTMBroker } from '@/service/gtm-broker';
import { LastViewedProducts } from '@/service/last-viewed-products';
import { ProductCollection } from '@/service/product';
import { Util } from '@/util/util';
import Vue from 'vue';
import { Api } from '@/util/api';
import { ProductPriceStock } from '@/model/product-price-stock';
import { Action } from '@/model/action';
import { Debouncer } from '@/util/debouncer';
import { RootVue } from '@/model/root-vue';
import { ToastMessage } from '@/service/toast-message';
import { translate } from '@/filters';
import { Config } from '@/model/config';

require('@fancyapps/fancybox');

interface VM {
  product: Product | null;
  lastViewedProducts: string[];
  orderAmount: number;
  pps: ProductPriceStock | null;
  loadingPrice: boolean;
  actions: Action[];
  quantityDiscounts: Action[];
  isMailSent: boolean;
  priceMessage: string
}

export default Vue.extend({
  name: 'ProductDetailControllerV2',
  components: {
    'product-item': () => import('../../components/product-item/ProductItem.vue'),
    'order-button': () => import('../../components/v2/order-button/OrderButton.vue'),
    'expandable-box': () => import('../../components/expandable-box/ExpandableBox.vue'),
    'quantity-input': () => import('../../components/pps-actions-module/QuantityInput.vue'),
    'pretty-price': () => import('../../components/pps-actions-module/PrettyPrice.vue'),
    'warehouse-stock': () => import('../../components/v2/pps-actions-module/WarehouseStock.vue'),
    'warehouse-stock-card': () => import('../../components/v2/pps-actions-module/WarehouseStockCard.vue'),
    'heuver-action': () => import('../../components/pps-actions-module/HeuverAction.vue'),
    spinner: () => import('../../components/pps-actions-module/Spinner.vue'),
    'calculate-action-discount': () => import('../../components/pps-actions-module/CalculateActionDiscount.vue'),
    'action-icon': () => import('../../components/pps-actions-module/ActionIcon.vue'),
    'html-to-pdf-downloader': () => import('../../components/pdf-download/HtmlToPdfDownloader.vue'),
    'pdf-download-link-v2': () => import('../../components/v2/pdf-download/PdfDownloadLinkV2.vue'),
    modal: () => import('../../components/v2/modal/Modal.vue'),
    'input-field': () => import('../../components/v2/fields/InputField.vue'),
    'product-image-with-thumbnails': () => import('../../components/v2/product-detail/ProductImageWithThumbnails.vue'),
    'product-specification-tabs': () => import('../../components/v2/product-detail/ProductSpecificationTabs.vue'),
    'product-specification-tab': () => import('../../components/v2/product-detail/ProductSpecificationTab.vue'),
    'expandable-box-v2': () => import('../../components/v2/ExpandableBox.vue'),
    'pretty-price-v2': () => import('../../components/v2/pps-actions-module/PrettyPrice.vue'),
    'suggested-products': () => import('../../components/suggested-products/SuggestedProducts.vue'),
    'quantity-input-v2': () => import('../../components/v2/pps-actions-module/QuantityInput.vue'),
    'pill-w-label': () => import('../../components/v2/PillWithLabel.vue'),
    'message-box': () => import('../../components/v2/MessageBox.vue'),
    'alternative-products': () => import('../../components/v2/AlternativeProducts.vue'),
    'product-shelf': () => import('../../components/v2/product-list/ProductShelf.vue'),
    'back-order': () => import('../../components/back-order/BackOrder.vue')
  },
  props: {
    productKey: {
      type: String,
      required: true
    },
    amount: {
      type: Number,
      default: 0
    },
    debouncer: {
      type: Object as () => Debouncer,
      default() {
        return new Debouncer();
      }
    },
    config: {
      type: Object as () => Config,
      default() {
        return Util.getConfig();
      }
    }
  },
  watch: {
    productKey() {
      this.refresh();
    },
    orderAmount() {
      if (this.product && !this.priceOnRequest) {
        // refresh prices/stocks for the new order amount (debounced)
        const me = this;
        this.$props.debouncer.debounce(function() {
          me._refreshPriceAndStockInformation();
        }, 500);
      }
    },
    pps() {
      if (this.product && this.pps && !this.priceOnRequest) {
        // refresh all actions for this pps
        this._refreshActions();
      }
    }
  },
  data(): VM {
    return {
      product: null,
      lastViewedProducts: [],
      orderAmount: 1,
      pps: null,
      loadingPrice: false,
      actions: [],
      quantityDiscounts: [],
      isMailSent: false,
      priceMessage: 'product.order.message.ecoTax'
    };
  },
  computed: {
    resourcePath(): string {
      return Util.getConfig().resourcePath;
    },
    /**
     * Returns the total stock.
     */
    stock(): number {
      if (this.pps) {
        return this.pps.totalStock;
      }
      return 0;
    },
    /**
     * Returns the item (unit) net price.
     */
    unitPrice(): number {
      if (this.pps && this.pps.itemNetPrice) {
        return this.pps.itemNetPrice;
      }
      return 0;
    },
    /**
     * Indicates whether the price is on request only
     */
    priceOnRequest(): boolean {
      if (this.pps && this.pps.priceOnRequest) {
        return this.pps.priceOnRequest;
      }
      return false;
    },
    /**
     * Returns the number of bonus points for the current selection.
     */
    bonusPoints(): number {
      if (this.pps && this.pps.lineTotalAmount && this.orderAmount > 0) {
        let points = Math.floor(this.pps.lineTotalAmount / 15.0);
        return points;
      }
      return 0;
    },
    /**
     * Indicates whether this product can be ordered.
     */
    canBeOrdered(): boolean {
      // T1910 0416 : Fix for product with price on request is not orderable AND stock is not being shown
      // product overview list/table
      return null != this.product
        ? this.product.isOrderable(this.stock, this.unitPrice, this.priceOnRequest) && this.product.showItem != 2
        : false;
    },
    /**
     * Indicates whether this product cannot be ordered, but a price should be shown.
     */
    showPriceOnly(): boolean {
      return null != this.product ? this.product.showPriceOnly(this.stock, this.unitPrice, this.priceOnRequest) : false;
    },
    /**
     * Show gross price for this product?
     */
    showGrossItemPrice(): boolean {
      return null != this.product && this.canBeOrdered && this.product.getShowGrossPrice() && this.product.grossPrice > this.unitPrice;
    },
    /**
     * Deducts the quantity increment step value for this product
     */
    quantityIncrementStepValue(): number {
      return null != this.product && null != this.product.multipleSalesQuantity && this.product.multipleSalesQuantity > 1
        ? this.product.multipleSalesQuantity
        : 1;
    },
    /**
     * Deducts the minimum quantity that can be ordered (it is an alias for "quantityIncrementStepValue").
     */
    quantityMinValue(): number {
      // what if no stock?
      return this.quantityIncrementStepValue;
    },
    /**
     * Deducts the maximum quantity that can be ordered (incorporating to be it a multiple of the increment step value).
     */
    quantityMaxValue(): number {
      if (null != this.product && null != this.pps) {
        const step: number = this.quantityIncrementStepValue;
        if (step > 1) {
          const remainder: number = this.pps.totalStock % step;
          if (remainder > 0) {
            return this.pps.totalStock - remainder;
          }
        }
        return this.pps.totalStock;
      }
      return 1;
    },
    showPriceMessage(): boolean {
      if (this.product) {
        return Util.showEcoTaxMessage(this.product.bu, this.product.productCategory, this.product.itemCondition)
      }
      return false
    }
  },
  mounted() {
    (this.$root as RootVue).vueInitialized = true;
    this.refresh();
    Api.getInstance()
      .isRelevant(this.$props.productKey)
      .then(x => {
        if (x) {
          LastViewedProducts.getInstance().add(this.$props.productKey);
        }
      });

    // Add alt tag in fancybox popup based on alt tag of clicked img
    (<any>$('[data-fancybox]')).fancybox({
      beforeShow: function(instance: any, current: any) {
        // This only works with timeout because of magic
        setTimeout(() => {
          current.$image.attr('alt', <string>current.opts.$orig.find('img').attr('alt'));
        }, 100);
      }
    });
  },
  methods: {
    mail() {
      if (this.product) {
        const _product = this.product;
        const amount = (this.$refs.modalAmount as any).value;
        const email = (this.$refs.modalEmail as any).value;
        const phone = (this.$refs.modalPhone as any).value;

        Api.getInstance()
          .sendMail(this.$props.productKey, _product.description, _product.eanCode, amount, email, phone)
          .then(() => {
            this.isMailSent = true;
          })
          .catch(() => {
            ToastMessage.getInstance().add(translate('toastMessage.extraInformation.error'));
          });
      }
    },

    addedToCart(articleCode: string, amount: number) {
      this._refreshPriceAndStockInformation(true);
      Util.shoppingCartToast(
        this.orderAmount,
        this.pps ? this.pps.totalStock : 0,
        this.pps ? this.pps.totalStockAvailable : 0,
        this.product
      );
    },
    calculateActionDiscountClicked(articleCode: string, amount: number) {
      // force a refresh
      if (amount !== this.orderAmount) {
        // will trigger a refresh
        this.orderAmount = amount;
      } else {
        // needs to be forced
        this._refreshPriceAndStockInformation(true);
      }
    },
    /**
     * Completely refreshes this component (both product as price/stock information)
     */
    refresh() {
      ProductCollection.getInstance()
        .getProduct(this.$props.productKey)
        .then(product => {
          this.product = product;
          this.orderAmount = this.quantityMinValue; // set minimum orderable value
          GTMBroker.getInstance().productDetailViewed(product);
          this._refreshPriceAndStockInformation();
        });
      this.lastViewedProducts = LastViewedProducts.getInstance().getAllExcept(this.$props.productKey);
    },
    /**
     * Only refreshes the price/stock information for this product and the selected order amount.
     */
    _refreshPriceAndStockInformation(force?: boolean) {
      if (!Util.isLoggedIn()) {
        return;
      }

      if (
        (undefined === force || !force) &&
        this.pps &&
        this.pps.quantity == this.orderAmount &&
        this.pps.articleCode == this.$props.productKey
      ) {
        // nothing has changed
        return;
      }

      if (this.orderAmount < 1) {
        // orderAmount will be zero, when no stock available...do not query prices again
        console.warn("No order amount available (value=" + this.orderAmount + ") will skip price/stock call");
        return;
      }

      // let's load new prices and stocks
      this.loadingPrice = true;

      // retrieve PPS data
      Api.getInstance()
        .getProductPriceStock(this.$props.productKey, this.orderAmount)
        .then((pps: ProductPriceStock) => {
          this.pps = pps;
          if (pps.totalStock < this.orderAmount) {
            // less stock left over as intended to be ordered...
            // is also triggered when no stock available, after the first call with the default orderAmount=1
            // orderAmount is watched, so putting it on another value will trigger a refresh
            this.orderAmount = this.quantityMaxValue;
          } else if (pps.quantity != this.orderAmount) {
            // mismatch in quantities (maybe a async data retrieval problem)...
            console.warn('Quantity mismatch: pps.quantity=' + pps.quantity + ', order amount=' + this.orderAmount);
            this.orderAmount = pps.quantity; // make sure we are synchronized!
          }
        })
        .finally(() => {
          this.loadingPrice = false;
        });
    },
    _refreshActions() {
      if (!Util.isLoggedIn()) {
        return;
      }

      if (this.pps) {
        // actions
        if (this.pps.actions && this.pps.actions.length > 0) {
          Api.getInstance()
            .getActionsByIds(this.pps.actions)
            .then((actions: Action[]) => {
              this.actions = actions;
            });
        } else {
          this.actions = [];
        }
        // quantity discounts
        if (this.pps.quantityDiscounts && this.pps.quantityDiscounts.length > 0) {
          Api.getInstance()
            .getQuantityDiscountsByIds(this.pps.quantityDiscounts)
            .then((actions: Action[]) => {
              this.quantityDiscounts = actions;
            });
        } else {
          this.quantityDiscounts = [];
        }
      } else {
        this.actions = [];
        this.quantityDiscounts = [];
      }
    }
  }
});
